import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DocumentExportAPIService } from 'src/app/extensions/services/api/documentExport-api.service';
import { InvoiceAPIService } from 'src/app/extensions/services/api/invoice-api.service';
import { PartnerAPIService } from 'src/app/extensions/services/api/partner-api.service';
import { ScrollService } from 'src/app/extensions/services/scroll.service';
import { InvoiceListContractComponent } from './invoice-list-contract/invoice-list-contract.component';
import { InvoiceListFilterComponent } from './invoice-list-filter.component.ts/invoice-list-filter.component';
import { base64StringToBlob } from 'blob-util';
import { NotificationService } from 'src/app/core/services/notification.service';
import { TranslateService } from '@ngx-translate/core';
import { RESPONSIVE_BREAKPOINTS } from 'src/app/extensions/constants/responsive-breakpoints';
import { WindowEventsService } from 'src/app/extensions/services/window-events.services';
import { Subscription } from 'rxjs';
import { scrollTop, setTableViewMode } from 'src/app/extensions/utilities';
import { GlobalService } from 'src/app/extensions/services/global.service';
import { TABLE_VIEW_MODES } from 'src/app/extensions/constants/commonConstants';
import { isEqual } from 'lodash';
import { FILTERS_RESET_TYPE } from 'src/app/extensions/constants/commonConstants';

@Component({
  selector: 'app-invoice-list',
  templateUrl: './invoice-list.component.html',
  styleUrls: ['./invoice-list.component.scss'],
  providers: [ScrollService],
  standalone: false,
})
export class InvoiceListComponent implements OnInit, OnDestroy {
  public invoices: app.models.InvoiceListModel[];
  public props: app.models.DynamicPropsDictionary;
  public subscriptions: Subscription[] = [];

  public FILTERS_RESET_TYPE = FILTERS_RESET_TYPE;
  private pageIndex = 1;
  private maxIndex: number;
  contractId: string;

  public activeFilterCount = 0;
  public contractFilterCount = 0;

  public loading: boolean = false;

  displayedTotalInvoices: app.models.TotalInvoiceModel[] = [];
  totalInvoices: app.models.TotalInvoiceModel[] = [];
  currentIndex = 0;
  visibleCardCount: number = 0;
  showArrows: boolean;

  public showBackToTop: boolean = false;

  public pageFilter: app.models.GetPagedInvoicesPageFilterModel;
  public initialFilterState: app.models.GetPagedInvoicesPageFilterModel;

  public currentRoute: string;
  public TABLE_VIEW_MODES = TABLE_VIEW_MODES;
  public tableViewMode: string;
  public hasData = true;
  public hasFilterData = true;
  public windowSizeLgUp: boolean;

  public client: app.models.Partner;

  public constructor(
    private _globalService: GlobalService,
    private _scrollService: ScrollService,
    private _invoiceAPIService: InvoiceAPIService,
    private _ngbModal: NgbModal,
    private _router: Router,
    private _partnerAPIService: PartnerAPIService,
    private _documentExportAPIService: DocumentExportAPIService,
    private _activatedRoute: ActivatedRoute,
    private _notificationService: NotificationService,
    private _translateService: TranslateService,
    private _windowEventService: WindowEventsService
  ) {
    this.currentRoute = this._router.url.split('/')[1];
  }

  public ngOnInit(): void {
    this.tableViewMode = setTableViewMode(this._globalService, this.currentRoute);

    this.pageFilter = {
      startDate: null,
      endDate: null,
      isPaid: false,
      isNotPaid: false,
      isOverdue: false,
      categories: [],
      contracts: [],
      contractTypeCodes: [],
      isOther: false,
      withoutContract: false,
      isInactiveContractsSelected: false,
    };
    this.initialFilterState = structuredClone(this.pageFilter);
    this._partnerAPIService.getClient().subscribe(result => {
      this.client = result;
    });

    this._activatedRoute.queryParams.subscribe(param => {
      this.contractId = param['contractId'];
      if (this.contractId != undefined) {
        this.pageFilter.contracts = [this.contractId];
      } else {
        this.pageFilter.contracts = [];
      }
      this.contractFilterCount = this.pageFilter.contracts.length;
      this.initialFilterState = structuredClone(this.pageFilter);
      this.loadData();
    });

    let backToTopSubscription = this._scrollService.obsShowBackToTop.subscribe(x => {
      this.showBackToTop = x;
    });
    this.subscriptions.push(backToTopSubscription);

    let scrollSubscription = this._scrollService.obsFetchData.subscribe(() => {
      if (this.pageIndex <= this.maxIndex) {
        this.pageIndex++;
        this._invoiceAPIService.getPagedInvoices(this.pageIndex, this.pageFilter).subscribe(x => {
          this.invoices = this.invoices.concat(x.data.values);

          if (this.pageIndex <= this.maxIndex) {
            this._scrollService.waitingForDataFetch = false;
          }

          this.updateVisibleCardCount();
          this.updateDisplayedCards();
        });
      }
    });
    this.subscriptions.push(scrollSubscription);

    this.windowSizeLgUp = window.innerWidth >= RESPONSIVE_BREAKPOINTS.lg;
    this.updateVisibleCardCount();
    let resizeSubscription = this._windowEventService.onResize.subscribe(() => {
      this.windowSizeLgUp = window.innerWidth >= RESPONSIVE_BREAKPOINTS.lg;
      this.updateVisibleCardCount();
      this.updateDisplayedCards();
    });
    this.subscriptions.push(resizeSubscription);
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.forEach(subscription => {
        subscription.unsubscribe();
      });
    }
  }

  loadData() {
    this._invoiceAPIService.getPagedInvoices(this.pageIndex, this.pageFilter).subscribe(x => {
      this.invoices = x.data.values;

      this.hasData = this.invoices.length != 0;
      this.props = x.props;
      this.maxIndex = x.data.totalPages;

      this._scrollService.waitingForDataFetch = this.pageIndex > this.maxIndex;
    });

    this._invoiceAPIService.getInvoicedSummary(this.pageFilter).subscribe(x => {
      this.totalInvoices = x.data.details.map(x => ({
        currencyCode: x.currencyCode,
        totalUnpaid: x.totalOutstandingAmount,
        totalInvoiced: x.totalInvoiced,
      }));

      this.updateVisibleCardCount();
      this.updateDisplayedCards();
    });
  }

  public toggleViewMode(tableViewMode: string) {
    this.tableViewMode = tableViewMode;
    this._globalService.toggleTableViewMode(this.currentRoute, tableViewMode);
  }

  public filterHandler() {
    let instance = this._ngbModal.open(InvoiceListFilterComponent, {
      scrollable: true,
    });
    instance.componentInstance.pageFilter = this.pageFilter;
    instance.componentInstance.initialFilterState = this.initialFilterState;

    instance.closed.subscribe(result => {
      if (result !== false) {
        this.initialFilterState = {
          ...this.initialFilterState,
          startDate: this.pageFilter.startDate,
          endDate: this.pageFilter.endDate,
          categories: this.pageFilter.categories,
          isOther: this.pageFilter.isOther,
          isPaid: this.pageFilter.isPaid,
          isNotPaid: this.pageFilter.isNotPaid,
          isOverdue: this.pageFilter.isOverdue,
        };
        this.pageIndex = 1;
        this.activeFilterCount = result;
        if (!isEqual(this.pageFilter, this.initialFilterState)) {
          this.pageFilter = structuredClone(this.initialFilterState);
        }
        this._invoiceAPIService.getPagedInvoices(this.pageIndex, this.pageFilter).subscribe(x => {
          this.invoices = x.data.values;

          this.hasFilterData = this.invoices.length != 0;
          this.hasData = this.invoices.length != 0;
          this.props = x.props;
          this.maxIndex = x.data.totalPages;

          this._scrollService.waitingForDataFetch = this.pageIndex > this.maxIndex;
        });

        this._invoiceAPIService.getInvoicedSummary(this.pageFilter).subscribe(x => {
          this.totalInvoices = x.data.details.map(x => ({
            currencyCode: x.currencyCode,
            totalUnpaid: x.totalOutstandingAmount,
            totalInvoiced: x.totalInvoiced,
          }));

          this.updateVisibleCardCount();
          this.updateDisplayedCards();
        });
      }
    });
  }

  public resetFiltersHandler(resetType: string = FILTERS_RESET_TYPE.TOTAL) {
    if (!this.contractId) {
      if ([FILTERS_RESET_TYPE.OTHER_FILTERS, FILTERS_RESET_TYPE.TOTAL].includes(resetType)) {
        this.pageFilter.contracts = [];
        this.pageFilter.contractTypeCodes = [];
        this.contractFilterCount = 0;
      }
      if ([FILTERS_RESET_TYPE.ACTIVE_FILTERS, FILTERS_RESET_TYPE.TOTAL].includes(resetType)) {
        this.pageFilter.startDate = null;
        this.pageFilter.endDate = null;
        this.pageFilter.isPaid = false;
        this.pageFilter.isNotPaid = false;
        this.pageFilter.isOverdue = false;
        this.pageFilter.categories = [];
        this.pageFilter.isOther = false;
        this.activeFilterCount = 0;
      }

      this.initialFilterState = {
        ...this.initialFilterState,
        startDate: this.pageFilter.startDate,
        endDate: this.pageFilter.endDate,
        categories: this.pageFilter.categories,
        isOther: this.pageFilter.isOther,
        isPaid: this.pageFilter.isPaid,
        isNotPaid: this.pageFilter.isNotPaid,
        isOverdue: this.pageFilter.isOverdue,
        contracts: this.pageFilter.contracts,
        withoutContract: this.pageFilter.withoutContract,
        contractTypeCodes: this.pageFilter.contractTypeCodes,
      };

      if (!isEqual(this.pageFilter, this.initialFilterState)) {
        this.pageFilter = structuredClone(this.initialFilterState);
      }

      this.hasFilterData = true;
      this.pageIndex = 1;
      this.loadData();
    } else {
      this._router.navigate(['invoices']);
    }
  }

  public contractHandler() {
    let instance = this._ngbModal.open(InvoiceListContractComponent, {
      scrollable: true,
    });
    instance.componentInstance.pageFilter = this.pageFilter;
    instance.componentInstance.initialFilterState = this.initialFilterState;

    instance.closed.subscribe(result => {
      if (result !== false) {
        this.initialFilterState = {
          ...this.initialFilterState,
          contracts: this.pageFilter.contracts,
          withoutContract: this.pageFilter.withoutContract,
          contractTypeCodes: this.pageFilter.contractTypeCodes,
        };
        this.pageIndex = 1;
        this.contractFilterCount = result;
        if (!isEqual(this.pageFilter, this.initialFilterState)) {
          this.pageFilter = structuredClone(this.initialFilterState);
        }
        this._invoiceAPIService.getPagedInvoices(this.pageIndex, this.pageFilter).subscribe(x => {
          this.invoices = x.data.values;

          this.hasData = this.invoices.length != 0;
          this.props = x.props;
          this.maxIndex = x.data.totalPages;

          this._scrollService.waitingForDataFetch = this.pageIndex > this.maxIndex;
        });

        this._invoiceAPIService.getInvoicedSummary(this.pageFilter).subscribe(x => {
          this.totalInvoices = x.data.details.map(x => ({
            currencyCode: x.currencyCode,
            totalUnpaid: x.totalOutstandingAmount,
            totalInvoiced: x.totalInvoiced,
          }));

          this.updateVisibleCardCount();
          this.updateDisplayedCards();
        });
      }
    });
  }

  public navigateToDetailHandler = (invoiceId: string) => {
    this._router.navigate(['invoices', invoiceId]);
  };

  public downloadInvoices() {
    this.loading = true;
    this._documentExportAPIService.exportInvoices(this.pageFilter, this.client).subscribe(
      file => {
        let blob = base64StringToBlob(file.response.buffer, file.response.contentType);
        let link = document.createElement('a');
        let objectUrl = URL.createObjectURL(blob);
        link.href = objectUrl;

        let dateTime = new Date();
        let date = dateTime.getFullYear() + '-' + (dateTime.getMonth() + 1) + '-' + dateTime.getDate();
        let time = dateTime.getHours() + '-' + dateTime.getMinutes() + '-' + dateTime.getSeconds();

        let generatedFileName = `i_${date}_${time}`;
        link.download = `${generatedFileName}.${file.response.contentType}`;
        link.click();
        this.loading = false;
      },
      () => {
        this.loading = false;
      }
    );
  }

  showClipboardNotification() {
    this._notificationService.showSuccess(
      this._translateService.instant('common.copiedToClipboard'),
      this._translateService.instant('common.success')
    );
  }

  prevSlide() {
    if (this.currentIndex > 0) {
      this.currentIndex -= this.visibleCardCount;
      this.updateDisplayedCards();
    }
  }

  nextSlide() {
    const maxIndex = this.totalInvoices.length - this.visibleCardCount;
    if (this.currentIndex < maxIndex) {
      this.currentIndex += this.visibleCardCount;
      this.updateDisplayedCards();
    }
  }

  updateVisibleCardCount() {
    if (window.innerWidth > RESPONSIVE_BREAKPOINTS.xxxl) {
      this.visibleCardCount = 2;
    } else {
      this.visibleCardCount = 1;
    }
    this.showArrows = this.totalInvoices.length > this.visibleCardCount;
  }

  updateDisplayedCards() {
    this.displayedTotalInvoices = this.totalInvoices.slice(
      this.currentIndex,
      this.currentIndex + this.visibleCardCount
    );
  }

  scrollToTop(): void {
    scrollTop();
  }
}
